import { Badge, Button, Dropdown, TextWithDescription } from '@ibsys/kit';
import { EarthIcon, MoonIcon, PaletteIcon, SunIcon, Undo2Icon, UserCircleIcon, VenetianMaskIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/hooks';
import { useStopImpersonation } from '@/entities/auth';
import { useMyProfile } from '@/entities/profile';
import { useSettings, useThemeConfig } from '@/entities/settings';
import { cn, getShortName, useAppNavigate } from '@/shared/lib';
import Avatar from './avatar_default.png';

export const ProfileDropdown: FC = () => {
  const { t } = useTranslation(['common']);
  const navigate = useAppNavigate();
  const { data } = useMyProfile();

  const { language, theme, setLanguage, setTheme } = useSettings();
  const { data: projectConfig } = useThemeConfig();
  const { logout } = useAuth();
  const { mutate: stopImpersonation } = useStopImpersonation();

  const impersonator = data?.impersonator;

  const handleStopImpersonation = () => {
    stopImpersonation(undefined, {
      // Перезагрузка страницы, а не переход внутри приложения: сессия сменилась,
      // и права, профиль и настройки интерфейса нужно перечитать с нуля
      onSuccess: () => {
        window.location.href = '/';
      },
    });
  };

  const themes = projectConfig?.themes ?? [];
  const currentThemeName = useMemo(() => {
    const found = themes.find((th) => th.code === theme);
    return found?.name ?? (theme === 'dark' ? t('themeDark') : t('themeLight'));
  }, [themes, theme, t]);

  const themeOptions = useMemo(() => {
    if (themes.length === 0) {
      return [
        {
          key: 'light',
          title: (
            <div className={'flex gap-1.5 items-center'}>
              <SunIcon size={16} />
              {t('themeLight')}
            </div>
          ),
          onClick: () => setTheme('light'),
          type: 'item' as const,
        },
        {
          key: 'dark',
          title: (
            <div className={'flex gap-1.5 items-center'}>
              <MoonIcon size={16} />
              {t('themeDark')}
            </div>
          ),
          onClick: () => setTheme('dark'),
          type: 'item' as const,
        },
      ];
    }
    return themes.map((th) => ({
      key: th.code,
      title: (
        <div className={'flex gap-1.5 items-center'}>
          {th.code === 'dark' ? (
            <MoonIcon size={16} />
          ) : th.code === 'light' ? (
            <SunIcon size={16} />
          ) : (
            <PaletteIcon size={16} />
          )}
          {th.name}
        </div>
      ),
      onClick: () => setTheme(th.code),
      type: 'item' as const,
    }));
  }, [themes, setTheme, t]);

  return (
    <Dropdown
      menuClassName={'flex flex-col gap-1.5 min-w-60'}
      options={[
        {
          key: 'name',
          type: 'label',
          title: data && (
            <TextWithDescription
              text={
                data && <span className={'text-sm text-foreground'}>{getShortName(data.firstName, data.lastName)}</span>
              }
              description={<span className={'text-xs text-light'}>{data.email}</span>}
            />
          ),
        },
        // Имя того, под кем идёт работа, уже в блоке выше — здесь только сам факт режима и его инициатор
        ...(impersonator
          ? [
              {
                key: 'impersonation',
                type: 'label' as const,
                title: (
                  <TextWithDescription
                    text={
                      <span className={'flex gap-1.5 items-center text-sm text-destructive'}>
                        <VenetianMaskIcon size={16} />
                        {t('impersonation.mode')}
                      </span>
                    }
                    description={
                      <span className={'text-xs text-light'}>
                        {t('impersonation.startedBy', {
                          name: getShortName(impersonator.firstName, impersonator.lastName),
                        })}
                      </span>
                    }
                  />
                ),
              },
            ]
          : []),
        { key: 'nameSeparator', type: 'separator' },
        {
          key: 'profile',
          title: (
            <div className={'flex gap-1.5 items-center'}>
              <UserCircleIcon />
              {t('routes.profile')}
            </div>
          ),
          type: 'item',
          onClick: () => navigate('/profile'),
        },
        {
          key: 'languages',
          title: (
            <div className={'flex gap-1.5 items-center w-full'}>
              <EarthIcon size={16} className={'text-muted-foreground'} />
              {t('languages')}
              <Badge variant={'secondary'} className={'ml-auto'}>
                {language === 'en' ? t('langEnglish') : t('langRussian')}
              </Badge>
            </div>
          ),
          type: 'item',
          children: [
            {
              key: 'english',
              title: <div className={'flex gap-1.5 items-center'}>{t('langEnglish')}</div>,
              onClick: () => setLanguage('en'),
              type: 'item',
            },
            {
              key: 'russian',
              title: <div className={'flex gap-1.5 items-center'}>{t('langRussian')}</div>,
              onClick: () => setLanguage('ru'),
              type: 'item',
            },
          ],
        },
        {
          key: 'theme',
          title: (
            <div className={'flex gap-1.5 items-center w-full'}>
              {theme === 'dark' ? (
                <MoonIcon size={16} className={'text-muted-foreground'} />
              ) : theme === 'light' ? (
                <SunIcon size={16} className={'text-muted-foreground'} />
              ) : (
                <PaletteIcon size={16} className={'text-muted-foreground'} />
              )}
              {t('theme')}
              <Badge variant={'secondary'} className={'ml-auto'}>
                {currentThemeName}
              </Badge>
            </div>
          ),
          type: 'item',
          children: themeOptions,
        },
        ...(impersonator
          ? [
              {
                key: 'stopImpersonation',
                type: 'item' as const,
                title: (
                  <div className={'flex gap-1.5 items-center'}>
                    <Undo2Icon size={16} />
                    {t('impersonation.stop')}
                  </div>
                ),
                onClick: handleStopImpersonation,
              },
            ]
          : []),
        {
          key: 'logout',
          title: (
            <Button size={'sm'} variant={'outline'} className={'flex gap-1.5 items-center w-full'} onClick={logout}>
              {t('logout')}
            </Button>
          ),
          type: 'label',
        },
      ]}
    >
      {/* Признак режима вынесен на аватар: он есть на любой странице, поэтому режим виден
          без открытия меню и не занимает места в узкой шапке.
          Кольцо намеренно толстое, с отступом от аватара и красным свечением — режим
          должен бросаться в глаза на любом фоне, а не читаться как обычная рамка */}
      <div
        data-impersonation-active={!!impersonator}
        className={cn(
          'rounded-4xl  overflow-hidden hover:scale-110 transition-all hover:shadow-md hover:shadow-primary ',
          impersonator &&
            'ring-4 ring-destructive ring-offset-2 ring-offset-background shadow-lg shadow-destructive hover:shadow-destructive',
        )}
      >
        <figure className={'size-10 '}>
          <img src={Avatar} alt={''} />
        </figure>
      </div>
    </Dropdown>
  );
};
